import { severityTextColor, SEVERITY_LABELS } from "./markers";
import type { SiteSeverity } from "@safers/api";

export interface PopoverSite {
  id: string;
  label: string;
  severity: SiteSeverity;
}

export interface PopoverState {
  x: number;
  y: number;
  sites: PopoverSite[];
  clusterKey?: string;
}

interface Props {
  state: PopoverState;
  containerWidth: number;
  containerHeight: number;
  onSitePick: (id: string) => void;
}

const BRAND = "var(--brand-primary)";
const CONNECTOR = 32;
const RADIUS = 8;

const LIST_W = 160;
const ROW_H = 28;
const LIST_PAD_Y = 6;

const EDGE = 4;

function clampTop(top: number, height: number, containerHeight: number) {
  return Math.min(Math.max(top, EDGE), Math.max(containerHeight - height - EDGE, EDGE));
}

export function MapPopover({ state, containerWidth, containerHeight, onSitePick }: Props) {
  const width = LIST_W;
  const height = LIST_PAD_Y * 2 + state.sites.length * ROW_H;

  const toLeft = state.x > containerWidth / 2;
  const left = toLeft ? state.x - CONNECTOR - width : state.x + CONNECTOR;
  const top = clampTop(state.y - height / 2, height, containerHeight);

  return (
    <>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute z-[900]"
        style={{
          left: toLeft ? state.x - CONNECTOR : state.x,
          top: state.y - 1,
          width: CONNECTOR + RADIUS,
          marginLeft: toLeft ? -RADIUS : 0,
          height: 2,
          background: BRAND,
        }}
      />

      <div
        className="absolute z-[900] overflow-hidden rounded-lg shadow-lg"
        style={{ left, top, width, minHeight: height, background: BRAND }}
      >
        <ul className="py-1.5">
          {state.sites.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onSitePick(s.id)}
                className="flex h-[1.75rem] w-full items-center gap-2 px-2 text-left hover:bg-black/10"
              >
                <span
                  aria-hidden="true"
                  className="h-2 w-2 flex-none rounded-full ring-1 ring-white/70"
                  style={{ background: severityTextColor(s.severity) }}
                />
                <span className="min-w-0 flex-1 truncate text-[0.6875rem] font-semibold text-white">
                  {s.label}
                </span>
                <span className="flex-none text-[0.625rem] font-medium text-white/80">
                  {SEVERITY_LABELS[s.severity]}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
